import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import { ReactComponent as CloseModalIcon } from "../../assets/icons/CloseModalIcon.svg";
import { showSuccessNotification } from "../../helper/Notifications";

const CustomModal = ({
  isOpen,
  onOpenChange,
  // New: simple type selector for built-in icons (e.g., "key", "warning", "success", "info", "error")
  iconType,
  iconAlignment = "center", // "left" | "center" | "right"
  headerLayout = "column",
  title,
  subtitle,
  titleAlignment = "left", // "left" | "center"
  children,
  buttons = [], // Array of button objects: { text, onClick, variant: "primary" | "secondary" | "delete", action?: async function, successMessage?: string }
  buttonLayout = "row", // "row" | "column"
  betweenButtonsContent, // optional ReactNode to render between first and remaining buttons when column layout
  showCloseButton = false,
  autoCloseOnSuccess = true, // Auto-close modal on successful action
  width, // Custom width in pixels (e.g., "400", "600")
  disableBackgroundClose = false, // Prevent closing on background click or ESC key
  zIndex = 1000, // Custom z-index (default: 1000)
}) => {
  const ref = useRef();
  const [loadingButtons, setLoadingButtons] = useState(new Set());

  const handleButtonClick = async (onClick, action, buttonIndex, successMessage) => {
    if (action) {
      // Handle async action with loading state
      setLoadingButtons(prev => new Set(prev).add(buttonIndex));

      try {
        await action();
        if (autoCloseOnSuccess) {
          onOpenChange?.(false);
        }
        // Show success notification if message is provided
        if (successMessage) {
          showSuccessNotification(successMessage);
        }
      } catch (error) {
        // Error handling - don't close modal, let user handle the error
        console.error('Modal action failed:', error);
      } finally {
        setLoadingButtons(prev => {
          const newSet = new Set(prev);
          newSet.delete(buttonIndex);
          return newSet;
        });
      }
    } else {
      // Handle regular onClick
      onClick?.();
    }
  };

  const getButtonVariant = (variant) => {
    switch (variant) {
      case "delete":
        return "delete";
      case "secondary":
        return "secondary";
      case "primary":
      default:
        return "primary";
    }
  };

  const handleClose = () => {
    if (!disableBackgroundClose) {
      onOpenChange?.(false);
    }
  };

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (isOpen && ref.current && !ref.current.contains(e.target) && !disableBackgroundClose) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [isOpen, disableBackgroundClose]);

  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27 && !disableBackgroundClose) {
        handleClose();
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [disableBackgroundClose]);

  // Built-in icons by type + default colors
  const getIconByType = (t) => {
    switch (t) {
      case "subscriptionPlus":
        return (
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4.5C12.5523 4.5 13 4.94772 13 5.5V11.5H19C19.5523 11.5 20 11.9477 20 12.5C20 13.0523 19.5523 13.5 19 13.5H13V19.5C13 20.0523 12.5523 20.5 12 20.5C11.4477 20.5 11 20.0523 11 19.5V13.5H5C4.44772 13.5 4 13.0523 4 12.5C4 11.9477 4.44772 11.5 5 11.5H11V5.5C11 4.94772 11.4477 4.5 12 4.5Z" fill="#7F56D9"/>
          </svg>
        );
      case "key":
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.9169 6.25033L15.8336 3.33366M17.5002 1.66699L15.8336 3.33366L17.5002 1.66699ZM9.4919 9.67533C9.92218 10.0999 10.2642 10.6054 10.4984 11.1627C10.7325 11.7199 10.8541 12.318 10.8561 12.9225C10.8581 13.527 10.7405 14.1259 10.5102 14.6847C10.2798 15.2436 9.94111 15.7514 9.51368 16.1788C9.08625 16.6062 8.5785 16.9449 8.01965 17.1753C7.4608 17.4056 6.8619 17.5232 6.25742 17.5212C5.65295 17.5192 5.05485 17.3976 4.49755 17.1635C3.94026 16.9293 3.43478 16.5873 3.01023 16.157C2.17534 15.2926 1.71336 14.1348 1.72381 12.9331C1.73425 11.7313 2.21627 10.5818 3.06606 9.73199C3.91585 8.8822 5.0654 8.40018 6.26714 8.38974C7.46888 8.37929 8.62664 8.84127 9.49106 9.67616L9.4919 9.67533ZM9.4919 9.67533L12.9169 6.25033L9.4919 9.67533ZM12.9169 6.25033L15.4169 8.75033L18.3336 5.83366L15.8336 3.33366L12.9169 6.25033Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case "warning":
        return (
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8.5V12.5M12 16.5H12.01M22 12.5C22 18.0228 17.5228 22.5 12 22.5C6.47715 22.5 2 18.0228 2 12.5C2 6.97715 6.47715 2.5 12 2.5C17.5228 2.5 22 6.97715 22 12.5Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case "warningTriangle":
        return (
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.9998 8.50021V12.5002M11.9998 16.5002H12.0098M10.2898 3.3602L1.81978 17.5002C1.64514 17.8026 1.55274 18.1455 1.55177 18.4947C1.55079 18.8439 1.64127 19.1873 1.8142 19.4907C1.98714 19.7941 2.2365 20.047 2.53748 20.2241C2.83847 20.4012 3.18058 20.4964 3.52978 20.5002H20.4698C20.819 20.4964 21.1611 20.4012 21.4621 20.2241C21.7631 20.047 22.0124 19.7941 22.1854 19.4907C22.3583 19.1873 22.4488 18.8439 22.4478 18.4947C22.4468 18.1455 22.3544 17.8026 22.1798 17.5002L13.7098 3.3602C13.5315 3.06631 13.2805 2.82332 12.981 2.65469C12.6814 2.48605 12.3435 2.39746 11.9998 2.39746C11.656 2.39746 11.3181 2.48605 11.0186 2.65469C10.7191 2.82332 10.468 3.06631 10.2898 3.3602Z" stroke="#DC6803" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case "success":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17L4 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case "info":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 17V11M12 7H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case "userPlus":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M20 8V14M23 11H17M12.5 7C12.5 9.20914 10.7091 11 8.5 11C6.29086 11 4.5 9.20914 4.5 7C4.5 4.79086 6.29086 3 8.5 3C10.7091 3 12.5 4.79086 12.5 7Z" stroke="#7F56D9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        );
      case "smile":
        // Provided fill-based smile icon. Uses currentColor to match theme.
        return (
          <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 20.5C4.477 20.5 0 16.023 0 10.5C0 4.977 4.477 0.5 10 0.5C15.523 0.5 20 4.977 20 10.5C20 16.023 15.523 20.5 10 20.5ZM10 18.5C12.1217 18.5 14.1566 17.6571 15.6569 16.1569C17.1571 14.6566 18 12.6217 18 10.5C18 8.37827 17.1571 6.34344 15.6569 4.84315C14.1566 3.34285 12.1217 2.5 10 2.5C7.87827 2.5 5.84344 3.34285 4.34315 4.84315C2.84285 6.34344 2 8.37827 2 10.5C2 12.6217 2.84285 14.6566 4.34315 16.1569C5.84344 17.6571 7.87827 18.5 10 18.5ZM6 11.5H14C14 12.5609 13.5786 13.5783 12.8284 14.3284C12.0783 15.0786 11.0609 15.5 10 15.5C8.93913 15.5 7.92172 15.0786 7.17157 14.3284C6.42143 13.5783 6 12.5609 6 11.5ZM6 9.5C5.60218 9.5 5.22064 9.34196 4.93934 9.06066C4.65804 8.77936 4.5 8.39782 4.5 8C4.5 7.60218 4.65804 7.22064 4.93934 6.93934C5.22064 6.65804 5.60218 6.5 6 6.5C6.39782 6.5 6.77936 6.65804 7.06066 6.93934C7.34196 7.22064 7.5 7.60218 7.5 8C7.5 8.39782 7.34196 8.77936 7.06066 9.06066C6.77936 9.34196 6.39782 9.5 6 9.5ZM14 9.5C13.6022 9.5 13.2206 9.34196 12.9393 9.06066C12.658 8.77936 12.5 8.39782 12.5 8C12.5 7.60218 12.658 7.22064 12.9393 6.93934C13.2206 6.65804 13.6022 6.5 14 6.5C14.3978 6.5 14.7794 6.65804 15.0607 6.93934C15.342 7.22064 15.5 7.60218 15.5 8C15.5 8.39782 15.342 8.77936 15.0607 9.06066C14.7794 9.34196 14.3978 9.5 14 9.5Z" fill="currentColor"/>
          </svg>
        );
      case "cpu":
        return (
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_33_34324)">
              <path d="M9.50016 3.83301V6.33301M14.5002 3.83301V6.33301M9.50016 19.6663V22.1663M14.5002 19.6663V22.1663M18.6668 10.4997H21.1668M18.6668 14.6663H21.1668M2.8335 10.4997H5.3335M2.8335 14.6663H5.3335M7.00016 6.33301H17.0002C17.9206 6.33301 18.6668 7.0792 18.6668 7.99967V17.9997C18.6668 18.9201 17.9206 19.6663 17.0002 19.6663H7.00016C6.07969 19.6663 5.3335 18.9201 5.3335 17.9997V7.99967C5.3335 7.0792 6.07969 6.33301 7.00016 6.33301ZM9.50016 10.4997H14.5002V15.4997H9.50016V10.4997Z" stroke="#957AE6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_33_34324">
                <rect width="20" height="20" fill="white" transform="translate(2 3)"/>
              </clipPath>
            </defs>
          </svg>
        );
      case "chip":
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_33_34325)">
              <path d="M7.50016 0.833008V3.33301M12.5002 0.833008V3.33301M7.50016 16.6663V19.1663M12.5002 16.6663V19.1663M16.6668 7.49967H19.1668M16.6668 11.6663H19.1668M0.833496 7.49967H3.3335M0.833496 11.6663H3.3335M5.00016 3.33301H15.0002C15.9206 3.33301 16.6668 4.0792 16.6668 4.99967V14.9997C16.6668 15.9201 15.9206 16.6663 15.0002 16.6663H5.00016C4.07969 16.6663 3.3335 15.9201 3.3335 14.9997V4.99967C3.3335 4.0792 4.07969 3.33301 5.00016 3.33301ZM7.50016 7.49967H12.5002V12.4997H7.50016V7.49967Z" stroke="#957AE6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_33_34325">
                <rect width="20" height="20" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        );
      case "error":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case "apiKey":
        return (
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 17.9678L12 22.9678L22 17.9678M2 12.9678L12 17.9678L22 12.9678M12 2.96777L2 7.96777L12 12.9678L22 7.96777L12 2.96777Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const getDefaultColorsByType = (t) => {
    switch (t) {
      case "subscriptionPlus":
        // Brand/100 background and Brand/50 border as specified
        return { bg: "#F4EBFF", fg: "#7F56D9", bd: "#F9F5FF" };
      case "key":
        return { bg: "#F4EBFF", fg: "#6941C6", bd: "#F9F5FF" };
      case "warning":
        return { bg: "#FEE3E2", fg: "#D92D20", bd: "#FEF3F2" };
      case "warningTriangle":
        return { bg: "#FEF0C7", fg: "#DC6803", bd: "#FFFAEB" };
      case "success":
        return { bg: "#ECFDF3", fg: "#027A48", bd: "#D1FADF" };
      case "info":
        return { bg: "#EFF8FF", fg: "#026AA2", bd: "#D1E9FF" };
      case "userPlus":
        return { bg: "#F4EBFF", fg: "#7F56D9", bd: "#F9F5FF" };
      case "smile":
        // Brand palette: bg Brand/100, border Brand/50, icon color from provided SVG
        return { bg: "#F4EBFF", fg: "#957AE6", bd: "#F9F5FF" };
      case "cpu":
        return { bg: "#F4EBFF", fg: "#957AE6", bd: "#F9F5FF" };
      case "chip":
        return { bg: "#F4EBFF", fg: "#957AE6", bd: "#F9F5FF" };
      case "apiKey":
        return { bg: "#F4EBFF", fg: "#7F56D9", bd: "#F9F5FF" };
      case "error":
        return { bg: "#FEE2E2", fg: "#B91C1C", bd: "#FECACA" };
      default:
        // Fallback styling when no iconType is provided
        return { bg: lightThemeColors.primaryColor, fg: "white", bd: "#E5E7EB" };
    }
  };

  const resolvedIcon = iconType ? getIconByType(iconType) : null;
  const defaults = iconType ? getDefaultColorsByType(iconType) : { bg: lightThemeColors.primaryColor, fg: "white", bd: "#E5E7EB" };
  const resolvedBgColor = defaults.bg;
  const resolvedIconColor = defaults.fg;
  const resolvedBorderColor = defaults.bd;
  const useStrokeMode = !(iconType === 'smile' || iconType === 'cpu' || iconType === 'chip' || iconType === 'subscriptionPlus');

  // Reset loading states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLoadingButtons(new Set());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <FullscreenContainer $zIndex={zIndex}>
      <ModalContainer ref={ref} $width={width}>
        <ModalHeader $alignment={titleAlignment} $headerLayout={headerLayout}>
          {(resolvedIcon) && (
            <IconContainer $alignment={iconAlignment} $headerLayout={headerLayout}>
              <IconWrapper $backgroundColor={resolvedBgColor} $iconColor={resolvedIconColor} $borderColor={resolvedBorderColor} $useStroke={useStrokeMode} $headerLayout={headerLayout}>
                {resolvedIcon}
              </IconWrapper>
            </IconContainer>
          )}

          {(title || subtitle) && (
            <TitleGroup>
              {title && (
                <ModalTitle $alignment={titleAlignment} $headerLayout={headerLayout}>
                  {title}
                </ModalTitle>
              )}

              {subtitle && (
                <ModalSubtitle $alignment={titleAlignment} $headerLayout={headerLayout}>
                  {subtitle}
                </ModalSubtitle>
              )}
            </TitleGroup>
          )}

          {showCloseButton && (
            <CloseButton onClick={handleClose}>
              <CloseModalIcon />
            </CloseButton>
          )}
        </ModalHeader>

        {children && (
          <ContentSlot>
            {children}
          </ContentSlot>
        )}

        {buttons.length > 0 && (
          <ButtonContainer $layout={buttonLayout}>
            {betweenButtonsContent && buttonLayout === 'column' && buttons.length >= 2 ? (
              <>
                {(() => {
                  const index = 0;
                  const button = buttons[index];
                  const isLoading = loadingButtons.has(index);
                  const hasAnyLoading = loadingButtons.size > 0;
                  const isDisabled = button.disabled || isLoading || (button.action && hasAnyLoading && !isLoading);
                  return (
                    <StyledButton
                      key={index}
                      $variant={getButtonVariant(button.variant)}
                      onClick={() => handleButtonClick(button.onClick, button.action, index, button.successMessage)}
                      disabled={isDisabled}
                    >
                      {isLoading && <Spinner />}
                      <ButtonText $isLoading={isLoading}>{button.text}</ButtonText>
                    </StyledButton>
                  );
                })()}
                <ButtonsMiddleContent>
                  {betweenButtonsContent}
                </ButtonsMiddleContent>
                {buttons.slice(1).map((button, sliceIdx) => {
                  const index = sliceIdx + 1;
                  const isLoading = loadingButtons.has(index);
                  const hasAnyLoading = loadingButtons.size > 0;
                  const isDisabled = button.disabled || isLoading || (button.action && hasAnyLoading && !isLoading);
                  return (
                    <StyledButton
                      key={index}
                      $variant={getButtonVariant(button.variant)}
                      onClick={() => handleButtonClick(button.onClick, button.action, index, button.successMessage)}
                      disabled={isDisabled}
                    >
                      {isLoading && <Spinner />}
                      <ButtonText $isLoading={isLoading}>{button.text}</ButtonText>
                    </StyledButton>
                  );
                })}
              </>
            ) : (
              buttons.map((button, index) => {
                const isLoading = loadingButtons.has(index);
                const hasAnyLoading = loadingButtons.size > 0;
                const isDisabled = button.disabled || isLoading || (button.action && hasAnyLoading && !isLoading);

                return (
                  <StyledButton
                    key={index}
                    $variant={getButtonVariant(button.variant)}
                    onClick={() => handleButtonClick(button.onClick, button.action, index, button.successMessage)}
                    disabled={isDisabled}
                  >
                    {isLoading && <Spinner />}
                    <ButtonText $isLoading={isLoading}>{button.text}</ButtonText>
                  </StyledButton>
                );
              })
            )}
          </ButtonContainer>
        )}
      </ModalContainer>
    </FullscreenContainer>
  );
};

export default CustomModal;

// Spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-right: 8px;
`;

const ButtonText = styled.span`
  ${props => props.$isLoading && `margin-left: 0;`}
`;

const FullscreenContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 6, 17, 0.7);
  backdrop-filter: blur(5px);
  z-index: ${props => props.$zIndex || 1000};
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 0;
`;

const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${lightThemeColors.background};
  max-width: ${props => props.$width ? `${props.$width}px` : '512px'};
  min-width: ${props => props.$width ? `min(${props.$width}px, 400px)` : '400px'};
  width: 100%;
  height: fit-content;
  margin: auto 20px;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  gap: 20px;

  @media (max-width: ${props => props.$width ? `${parseInt(props.$width) + 40}px` : '552px'}) {
    margin: 0 10px;
    min-width: unset;
    width: calc(100% - 20px);
  }
`;

const ModalHeader = styled.div`
  position: relative;
  display: flex;
  flex-direction: ${props => props.$headerLayout === "row" ? "row" : "column"};
  align-items: ${props => {
    if (props.$headerLayout === "row") return "center";
    switch (props.$alignment) {
      case "left":
        return "flex-start";
      case "center":
      default:
        return "center";
    }
  }};
  gap: ${props => props.$headerLayout === "row" ? "10px" : "20px"};
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: ${props => {
    switch (props.$alignment) {
      case "left":
        return "flex-start";
      case "right":
        return "flex-end";
      case "center":
      default:
        return "center";
    }
  }};
  width: ${props => props.$headerLayout === "row" ? "auto" : "100%"};
  flex-shrink: 0;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 28px;
  background-color: ${props => props.$backgroundColor};
  border: 8px solid ${props => props.$borderColor ?? 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.$iconColor};

    ${props => props.$useStroke ? `
      fill: none;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;

      * {
        fill: none !important;
        stroke: currentColor !important;
      }
    ` : `
      /* Fill-based icons should respect their fill */
    `}
  }
`;

const ModalTitle = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: ${props => props.$headerLayout === "row" ? "17px" : "20px"};
  font-weight: 600;
  line-height: ${props => props.$headerLayout === "row" ? "22px" : "24px"};
  text-align: ${props => props.$alignment};
  margin: 0;
`;

const ModalSubtitle = styled.p`
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: ${props => props.$headerLayout === "row" ? "13px" : "16px"};
  font-weight: 400;
  line-height: ${props => props.$headerLayout === "row" ? "18px" : "24px"};
  text-align: ${props => props.$alignment};
  margin: 0;
  margin-top: -12px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -10px;
  right: -10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${lightThemeColors.darkColor};
  }
`;

const ContentSlot = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  line-height: 24px;

  p {
    margin: 0;
  }

  strong {
    font-weight: 600;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  flex-direction: ${props => props.$layout === "column" ? "column" : "row"};
  justify-content: stretch;
  align-items: stretch;
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px; /* Figma padding */
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s;
  /* auto width buttons, not stretched */
  min-height: 44px; /* Figma height */
  min-width: 0; /* allow equal flex sizing without overflow */
  flex: 1; /* split available space equally */
  
  ${props => {
    switch (props.$variant) {
      case "delete":
        return `
          background-color: #ef4444;
          color: white;

          &:hover:not(:disabled) {
            background-color: #dc2626;
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `;
      case "secondary":
        return `
          background-color: #FFFFFF;
          color: ${lightThemeColors.darkColor};
          border: 1px solid #D5D7DA; /* Gray/300 */
          box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05); /* Shadow/xs */

          &:hover:not(:disabled) {
            background-color: #F9FAFB;
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `;
      case "primary":
      default:
        return `
          background-color: #7F56D9; /* Brand/600 */
          color: #FFFFFF;
          border: 1px solid #7F56D9; /* Figma border */
          box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05); /* Shadow/xs */

          &:hover:not(:disabled) {
            background-color: #6941C6; /* Brand/700 */
          }

          &:disabled {
            background-color: #E9D7FE; /* Brand/200 */
            color: #FFFFFF;
            border-color: #E9D7FE;
            cursor: not-allowed;
          }
        `;
    }
  }}
`;

const ButtonsMiddleContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085; /* Gray/600 */
  font-size: 14px;
  line-height: 20px;
`;
